import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import type {
  BranchesResponse,
  CircleListResponse,
  CircleSchedule,
  CircleSummary,
  Gender,
  ProgramsResponse,
  TimeWindow,
} from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, useSubmit } from "../components/ui";
import { api, errorText, send } from "../lib/api";
import { genderLabel } from "../lib/labels";
import { useCan } from "../lib/org";
import { useMe } from "../lib/session";

export function CirclesPage() {
  const { data: me } = useMe();
  const can = useCan();
  const search = useSearch({ from: "/app/circles" });
  const navigate = useNavigate({ from: "/circles" });
  const [creating, setCreating] = useState(false);
  const branches = useQuery({
    queryKey: ["branches", me?.activeRole],
    queryFn: () => api<BranchesResponse>("/v1/branches"),
  });
  const circles = useQuery({
    queryKey: ["circles", me?.activeRole, search.branchId ?? "all", search.archived ?? false],
    queryFn: () =>
      api<CircleListResponse>(
        `/v1/circles?${new URLSearchParams({
          ...(search.branchId ? { branchId: search.branchId } : {}),
          ...(search.archived ? { includeArchived: "true" } : {}),
        })}`,
      ),
  });

  return (
    <>
      <PageTitle
        title="الحلقات"
        sub="الحلقات ومعلموها وطلابها ضمن نطاقك"
        actions={
          can.has("circles.manage") && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Plus size={18} strokeWidth={1.75} /> حلقة جديدة
            </button>
          )
        }
      />
      <section className="card">
        <div className="toolbar">
          <label className="muted small" htmlFor="c-branch-filter">
            المجمع
          </label>
          <select
            id="c-branch-filter"
            value={search.branchId ?? ""}
            onChange={(e) => void navigate({ search: { ...search, branchId: e.target.value || undefined } })}
          >
            <option value="">كل المجمعات</option>
            {branches.data?.branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <label className="check small">
            <input
              type="checkbox"
              checked={!!search.archived}
              onChange={(e) =>
                void navigate({ search: { ...search, archived: e.target.checked || undefined } })
              }
            />
            إظهار المؤرشفة
          </label>
        </div>
        {circles.isPending ? (
          <Loading />
        ) : circles.isError ? (
          <Empty title="تعذر التحميل">{errorText(circles.error)}</Empty>
        ) : circles.data.circles.length === 0 ? (
          <Empty title="لا توجد حلقات">
            {can.has("circles.manage") ? "أنشئ حلقة جديدة للبدء." : undefined}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الحلقة</th>
                  <th>المجمع</th>
                  <th>البرنامج</th>
                  <th>الفئة</th>
                  <th>المعلم</th>
                  <th>الطلاب</th>
                </tr>
              </thead>
              <tbody>
                {circles.data.circles.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link to="/circles/$circleId" params={{ circleId: c.id }}>
                        {c.name}
                      </Link>{" "}
                      {c.archivedAt && <Badge>مؤرشفة</Badge>}
                    </td>
                    <td>{c.branchName}</td>
                    <td>{c.programName ?? <span className="muted">—</span>}</td>
                    <td>
                      {c.gender ? <Badge tone="brand">{genderLabel[c.gender]}</Badge> : <Badge>مختلطة</Badge>}
                    </td>
                    <td>
                      {c.teachers.length ? (
                        c.teachers.map((t) => t.name).join("، ")
                      ) : (
                        <Badge tone="warning">بلا معلم</Badge>
                      )}
                    </td>
                    <td className="num">
                      {c.studentCount}
                      {c.capacity ? <span className="muted"> / {c.capacity}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <CircleForm open={creating} onClose={() => setCreating(false)} defaultBranchId={search.branchId} />
    </>
  );
}

/** Create a circle, or edit one when `circle` is given. */
export function CircleForm({
  open,
  onClose,
  circle,
  defaultBranchId,
}: {
  open: boolean;
  onClose: () => void;
  circle?: CircleSummary;
  defaultBranchId?: string;
}) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const { busy, error, run } = useSubmit();
  const blank = () => ({
    name: circle?.name ?? "",
    branchId: circle?.branchId ?? defaultBranchId ?? "",
    gender: (circle?.gender ?? "") as Gender | "",
    capacity: circle?.capacity ? String(circle.capacity) : "",
    programId: circle?.programId ?? "",
    studyTime: circle?.studyTime ?? "",
    schedule: circle?.schedule ?? { open: false, periods: [{ from: "16:00", to: "17:00" }] },
  });
  const [form, setForm] = useState(blank);
  const [openedFor, setOpenedFor] = useState<boolean>(false);
  if (open !== openedFor) {
    setOpenedFor(open);
    if (open) setForm(blank());
  }
  const branches = useQuery({
    queryKey: ["branches", me?.activeRole],
    queryFn: () => api<BranchesResponse>("/v1/branches"),
    enabled: open,
  });
  const programs = useQuery({
    queryKey: ["programs", me?.activeRole],
    queryFn: () => api<ProgramsResponse>("/v1/programs"),
    enabled: open,
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      const common = {
        name: form.name.trim(),
        gender: form.gender || null,
        capacity: form.capacity ? Number(form.capacity) : null,
        programId: form.programId || null,
        studyTime: form.studyTime || null,
        schedule: form.schedule.open ? { open: true, periods: [] } : form.schedule,
      };
      if (circle) {
        await send("PATCH", `/v1/circles/${circle.id}`, common);
        await qc.invalidateQueries({ queryKey: ["circles"] });
        onClose();
      } else {
        const { id } = await send<{ id: string }>("POST", "/v1/circles", {
          ...common,
          branchId: form.branchId,
        });
        await qc.invalidateQueries({ queryKey: ["circles"] });
        await qc.invalidateQueries({ queryKey: ["overview"] });
        onClose();
        await navigate({ to: "/circles/$circleId", params: { circleId: id } });
      }
    }, errorText);
  }

  return (
    <Modal title={circle ? "تعديل الحلقة" : "حلقة جديدة"} open={open} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field id="c-name" label="اسم الحلقة">
          <input
            id="c-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </Field>
        {!circle && (
          <Field id="c-branch" label="المجمع">
            <select
              id="c-branch"
              value={form.branchId}
              onChange={(e) => setForm({ ...form, branchId: e.target.value })}
              required
            >
              <option value="">اختر المجمع</option>
              {branches.data?.branches
                .filter((b) => !b.archivedAt)
                .map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
            </select>
          </Field>
        )}
        <div className="form-row">
          <Field id="c-gender" label="الفئة" hint="حلقة لجنس واحد تقبل معلمين وطلابًا من الجنس نفسه فقط">
            <select
              id="c-gender"
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value as Gender | "" })}
            >
              <option value="">مختلطة / غير محددة</option>
              <option value="male">{genderLabel.male}</option>
              <option value="female">{genderLabel.female}</option>
            </select>
          </Field>
          <Field id="c-capacity" label="السعة" hint="للعرض فقط؛ لا تمنع التسجيل">
            <input
              id="c-capacity"
              type="number"
              min={1}
              max={500}
              inputMode="numeric"
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: e.target.value })}
            />
          </Field>
        </div>
        <Field
          id="c-time"
          label="وقت الحلقة"
          hint="يصل الطالب تذكير بورد اليوم قبله بنصف ساعة إذا لم يسمّع، ويستطيع تغيير وقت تذكيره من حسابه"
        >
          <input
            id="c-time"
            type="time"
            className="ltr"
            value={form.studyTime}
            onChange={(e) => setForm({ ...form, studyTime: e.target.value })}
          />
        </Field>
        <ScheduleField value={form.schedule} onChange={(schedule) => setForm({ ...form, schedule })} />
        <Field id="c-program" label="البرنامج">
          <select
            id="c-program"
            value={form.programId}
            onChange={(e) => setForm({ ...form, programId: e.target.value })}
          >
            <option value="">بدون برنامج</option>
            {programs.data?.programs
              .filter((p) => !p.archivedAt || p.id === form.programId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </Field>
        <div className="form-actions">
          <button
            className="btn btn-primary"
            disabled={busy || form.name.trim().length < 2 || !form.branchId}
          >
            {busy ? "جارٍ الحفظ…" : "حفظ"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * When students request live recitation (owner decision 2026-11-03): fixed periods, or open at any time.
 * Teachers take requests in these periods whatever their own hours, and their hours must include them.
 */
function ScheduleField({ value, onChange }: { value: CircleSchedule; onChange(v: CircleSchedule): void }) {
  const periods = value.periods.length ? value.periods : [{ from: "16:00", to: "17:00" }];
  const setPeriod = (i: number, patch: Partial<TimeWindow>) =>
    onChange({ open: false, periods: periods.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  return (
    <fieldset className="schedule">
      <legend>أوقات التسميع المباشر</legend>
      <div className="seg" role="radiogroup">
        <label>
          <input
            type="radio"
            name="c-open"
            checked={!value.open}
            onChange={() => onChange({ open: false, periods })}
          />{" "}
          فترات محددة
        </label>
        <label>
          <input
            type="radio"
            name="c-open"
            checked={value.open}
            onChange={() => onChange({ open: true, periods: value.periods })}
          />{" "}
          مفتوحة في أي وقت
        </label>
      </div>
      {!value.open && (
        <>
          {periods.map((p, i) => (
            <div className="form-row period-row" key={i}>
              <Field id={`c-from-${i}`} label="من">
                <input
                  id={`c-from-${i}`}
                  type="time"
                  className="ltr"
                  value={p.from}
                  onChange={(e) => setPeriod(i, { from: e.target.value })}
                  required
                />
              </Field>
              <Field id={`c-to-${i}`} label="إلى">
                <input
                  id={`c-to-${i}`}
                  type="time"
                  className="ltr"
                  value={p.to}
                  onChange={(e) => setPeriod(i, { to: e.target.value })}
                  required
                />
              </Field>
              {periods.length > 1 && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => onChange({ open: false, periods: periods.filter((_, j) => j !== i) })}
                >
                  حذف
                </button>
              )}
            </div>
          ))}
          {periods.length < 6 && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => onChange({ open: false, periods: [...periods, { from: "20:00", to: "21:00" }] })}
            >
              + فترة أخرى
            </button>
          )}
        </>
      )}
      <p className="muted small">
        {value.open
          ? "يطلب الطلاب التسميع متى شاؤوا، ويستقبل المعلم الطلبات في الأوقات التي يحددها هو."
          : "تصل طلبات الطلاب للمعلم في هذه الفترات دائمًا، ولا يُقبل من المعلم وقت استقبال لا يشملها."}
      </p>
    </fieldset>
  );
}
